/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    DocumentsAction,
    DocumentsActionType,
    downloadDocumentsListAsExcelErrorAction,
    downloadDocumentsListAsExcelSuccessAction,
    fetchDocumentsListPageErrorAction,
    fetchDocumentsListPageSuccessAction,
    fetchExtendedDataSuccessAction,
    fetchExtendedDataErrorAction,
} from '../actions/documentsActions';
import { RootState } from '../store.typings';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { DocumentsListRequestParams } from '../api/documents/documentsClient.typings';
import { DocumentsState } from '../reducers/documentsReducer';
import { from, of } from 'rxjs';
import { documentsClient } from '../api/documents/documentsClient';
import { companiesClient } from '../api/companies/companiesClient';
import { AxiosError } from 'axios';
import { parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

type DocumentsEpic = Epic<DocumentsAction, DocumentsAction, RootState>;

export const fetchDocumentsListPageEpic: DocumentsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(DocumentsActionType.FETCH_NEXT_PAGE_START, DocumentsActionType.APPLY_FILTERS),
        switchMap(() => {
            const state = state$.value.documentsReducer;
            const searchParams = makeDocumentsListRequestParams(state);

            return from(documentsClient.listDocuments(searchParams)).pipe(
                map((list) => fetchDocumentsListPageSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(
                        fetchDocumentsListPageErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.documentsNotFound')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(DocumentsActionType.EXIT_REGISTRY_DOCUMENTS_LIST_PAGE)),
            );
        }),
    );
};

function makeDocumentsListRequestParams(state: DocumentsState): DocumentsListRequestParams {
    return {
        ...state.filterParams,
        limit: state.limit,
        afterKeyCreatedAtUtc: state.afterKeyCreatedAtUtc,
        afterKeyId: state.afterKeyId,
    };
}

export const fetchDocumentsExtendedDataEpic: DocumentsEpic = (action$) => {
    return action$.pipe(
        ofType(DocumentsActionType.FETCH_EXTENDED_DATA_START),
        switchMap((action) => {
            return from(companiesClient.fetchCashboxesByTaxNumbers(action.payload)).pipe(
                map((list) => fetchExtendedDataSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(
                        fetchExtendedDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.documentsExtendedDataNotFound')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(DocumentsActionType.EXIT_REGISTRY_DOCUMENTS_LIST_PAGE)),
            );
        }),
    );
};

export const downloadDocumentsListEpic: DocumentsEpic = (action$, state) => {
    return action$.pipe(
        ofType(DocumentsActionType.DOWNLOAD_EXCEL_START),
        switchMap(() => {
            return from(
                documentsClient.downloadDocumentsAsExcel({ ...state.value.documentsReducer.filterParams }),
            ).pipe(
                map(() => downloadDocumentsListAsExcelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadDocumentsListAsExcelErrorAction(
                            parseError(err, i18n.t('downloadings.errors.requestToCreateFileFailed')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(DocumentsActionType.EXIT_REGISTRY_DOCUMENTS_LIST_PAGE)),
            );
        }),
    );
};
